import React, { useEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { useParams } from 'react-router-dom';
import { Button, LinearProgress } from '@material-ui/core';
import VoipTestNowTestResultsPanel from '../../panels/voip-test-now-panels/VoipTestNowTestResultsPanel';
import { Actions, Page, PageHeader, PanelContainer } from '../../page-framework';
import { useNavigate } from '../../../utilities/PgmUtilities';
import ReportAction from '../../page-framework/actions/ReportAction';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import { useDataService } from '../../../services/ApiDataService';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import {
    NetTestNowIsLoadingConsumer,
    NetTestNowIsLoadingProvider,
} from '../net-test-now-page/NetTestNowIsLoadingContext';
import { QUICK_TEST_STATUS } from '../../panels/quick-test-panel/QuickTestConstants';
import { rethrowError } from '../../../utilities/RequestUtilities';

export const pageId = 'quickTests/voip';

export default function VoipTestNowResultsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const resultPanelRef = useRef();
    const handleCancelTest = async () => {
        const { test } = resultPanelRef.current.getResults();
        const nextTest = { ...test, status: QUICK_TEST_STATUS.CANCELED };
        await dataService.put(`/ipm/v1/admin/quicktests/${test._id}`, nextTest, null, null, rethrowError);
        navigate('/quickTests/voip');
    };
    const handleReRunTest = () => {
        navigate('/quickTests/voip', { state: { isDetailsPanel: true, testSelection: id } });
    };
    const [testName, setTestName] = useState();
    const [NetTestNowIsLoading, setNetTestNowIsLoading] = useState(false);
    const [cancelTestDisabled, setCancelTestDisabled] = useState(false);
    const [reRunDisabled, setReRunDisabled] = useState(false);
    const dataService = useDataService();
    const goToUrl = '/quickTests/voip';
    const apiUrl = '/ipm/v1/admin/quicktests';
    const functionBreadCrumbs = [{ link: goToUrl, text: t`VoIP Test Now` }];
    const reportingLinkConfig = {
        target: '/quickTests/voip',
    };
    const { triggerRef } = useIntervalRefreshTrigger({
        interval: 20000,
    });

    useEffect(() => {
        (async () => {
            const quickTests = await dataService.get(apiUrl);
            const matchedTest = quickTests?.find((test) => test._id === id);
            setTestName(matchedTest?.name || '');
        })();
    }, [dataService, id]);

    const breadcrumbs = useBreadcrumb(
        functionBreadCrumbs,
        null,
        {},
        'name',
        `${testName ? `Results: ${testName}` : 'Test Result'}`,
        {
            reportingLinkConfig,
        }
    );

    return (
        <Page id={pageId}>
            <NetTestNowIsLoadingProvider value={[NetTestNowIsLoading, setNetTestNowIsLoading]}>
                <PageHeader
                    title={t`VoIP Test Now Result`}
                    breadcrumbs={breadcrumbs}
                    actions={
                        <Actions>
                            <Button onClick={handleCancelTest} disabled={cancelTestDisabled}>
                                <Trans>Cancel Test</Trans>
                            </Button>
                            <Button onClick={handleReRunTest} disabled={reRunDisabled}>
                                <Trans>Re-Run</Trans>
                            </Button>
                            <ReportAction
                                pageId={pageId}
                                pageTitle={t`VoIP Test Now Result`}
                                options={{ canSchedule: false }}
                                tooltipPlacement="bottom"
                            />
                        </Actions>
                    }
                />
                <NetTestNowIsLoadingConsumer>
                    {([testNowQueryIsLoading]) => <>{testNowQueryIsLoading && <LinearProgress color="secondary" />}</>}
                </NetTestNowIsLoadingConsumer>
                <PanelContainer flexHeight>
                    <VoipTestNowTestResultsPanel
                        id={id}
                        ref={resultPanelRef}
                        hideTitle
                        triggerRef={triggerRef}
                        setCancelTestDisabled={setCancelTestDisabled}
                        setReRunDisabled={setReRunDisabled}
                        onCancelTest={handleCancelTest}
                    />
                </PanelContainer>
            </NetTestNowIsLoadingProvider>
        </Page>
    );
}
